/*
 * hp-pdp-announce — says out loud what a variant change just did.
 *
 * The counterpart to hp-variant-transition.js. That file makes a variant change
 * read as ONE event for someone watching; this one makes it exist at all for
 * someone listening. Same event, same problem, two audiences.
 *
 * ── WHAT WAS SILENT ─────────────────────────────────────────────────────────
 * Selecting a colour changes the price, the availability and the button label.
 * A screen reader announces the radio ("Sand, selected") and then nothing at
 * all, so the one thing the shopper is choosing BETWEEN — what it costs and
 * whether they can have it — never reaches them. There was no live region
 * anywhere in hp-main-product.liquid, hp-subscription.liquid or
 * product-info.js.
 *
 * ── IT LISTENS FOR `variant:change`, NOT FOR THE PICKER'S `change` ──────────
 * The picker fires immediately; the price and the buy row arrive up to ~315ms
 * later behind a Section Rendering fetch (measured in hp-variant-transition.js).
 * Announcing on the picker would therefore read the OLD price out loud with
 * complete confidence. `variant:change` is dispatched by product-info.js AFTER
 * every swap has landed, which is the only moment the DOM agrees with itself.
 *
 * ── THE MESSAGE IS BUILT FROM TEXT ALREADY ON THE PAGE ──────────────────────
 * The price is read back out of the rendered price element and the availability
 * out of the submit button's own label. Nothing here formats money, and nothing
 * here holds a translation:
 *
 *   - Money formatting in JavaScript would be a second implementation of
 *     snippets/hp-price.liquid, which CLAUDE.md already warns about for the
 *     product card's swatch states. Reading the rendered value cannot disagree
 *     with what is on screen.
 *   - The button already says "Add to Cart" or "Sold out" in the shopper's
 *     language, because Liquid rendered it and base's `toggleSubmitButton`
 *     maintains it. Composing from it means this works in all ten locales with
 *     no new keys, and cannot drift from the control it describes.
 *
 * The variant title leads because a second option dimension can change what is
 * available in the first, so the configuration is not always what was clicked.
 *
 * ── READING A PRICE COMPONENT IS NOT `textContent`, AND THE FIRST PASS WAS ───
 * base's price component renders BOTH a regular and a sale block and hides one
 * with `display: none`. A plain `textContent` therefore reads both, and the
 * first version of this file announced
 *
 *     "Pink. Regular price $98.00 Regular price Sale price $98.00.
 *      Add to Cart - $98.00"
 *
 * for a product with no sale on it. Two separate faults in one string:
 *
 *   1. THE HIDDEN BRANCH IS STILL IN THE DOM. `visibleText` skips any subtree
 *      computed as `display: none` or `visibility: hidden`, which leaves the
 *      one block that is actually on screen.
 *   2. BUT `.visually-hidden` MUST BE KEPT. The "Regular price" and "Sale
 *      price" labels inside it are not noise, they are the whole reason a
 *      discounted price is intelligible without sight. Stripping them — the
 *      obvious next move after fault 1 — turns a sale into the bare, ambiguous
 *      "$98.00 $79.00". They survive the filter because `.visually-hidden` is
 *      `position: absolute` with a clip rect, never `display: none`.
 *
 * The CTA needs the same care for the opposite reason: it carries the price in
 * a SECOND span ("Add to Cart" + " - $98.00", a Houseplant addition — see
 * snippets/buy-buttons.liquid), so reading the whole button would say the price
 * twice. The first child span is the localized label alone, which is the seam
 * to use rather than splitting the string on its separator.
 */
(function () {
  'use strict';

  /* Text a screen reader would actually reach: hidden BRANCHES are skipped,
     visually-hidden LABELS are kept. See the note above for why both halves
     matter. */
  function visibleText(el) {
    if (!el) return '';
    var out = '';
    (function walk(node) {
      for (var i = 0; i < node.childNodes.length; i++) {
        var child = node.childNodes[i];
        if (child.nodeType === 3) {
          out += child.nodeValue;
        } else if (child.nodeType === 1) {
          var style = window.getComputedStyle(child);
          if (style.display === 'none' || style.visibility === 'hidden') continue;
          walk(child);
        }
      }
    })(el);
    return out.replace(/\s+/g, ' ').trim();
  }

  /* The button's localized label WITHOUT the price span beside it. */
  function ctaLabel(root, sectionId) {
    var button = root.querySelector('#ProductSubmitButton-' + sectionId);
    if (!button) return '';
    return visibleText(button.querySelector(':scope > span') || button);
  }

  /* The event is dispatched on `document`, so the host has to be found rather
     than read off the target. It is matched the way product-info.js computes
     its own `sectionId` (`data-original-section || data-section`) — those two
     differ wherever a section is rendered under a borrowed id, and building
     the root id from the wrong one silently scopes the lookup to nothing. */
  function hostFor(sectionId) {
    var hosts = document.querySelectorAll('product-info');
    for (var i = 0; i < hosts.length; i++) {
      var id = hosts[i].dataset.originalSection || hosts[i].dataset.section;
      if (id === sectionId) return hosts[i];
    }
    return document;
  }

  document.addEventListener('variant:change', function (event) {
    var detail = event.detail || {};
    var sectionId = detail.sectionId;
    if (!sectionId) return;

    var section = hostFor(sectionId);

    /* An unavailable COMBINATION resolves to no variant at all, which is a
       state worth announcing rather than a reason to stay quiet — it is
       precisely when the page looks most broken without a word. */
    if (!detail.variant) {
      window.hpAnnounce(ctaLabel(section, sectionId));
      return;
    }

    var parts = [];

    /* Single-variant products have the literal title "Default Title" and no
       picker, so there is nothing a shopper chose and nothing to repeat. */
    if (detail.variant.title && detail.variant.title !== 'Default Title') {
      parts.push(detail.variant.title.split(' / ').join(', '));
    }

    var price = visibleText(section.querySelector('#price-' + sectionId));
    if (price) parts.push(price);

    var cta = ctaLabel(section, sectionId);
    if (cta) parts.push(cta);

    /* Full stops so it is read as three facts rather than one run-on phrase. */
    window.hpAnnounce(parts.join('. '));
  });
})();
